import { createSlice } from '@reduxjs/toolkit';

/**
 * Interface representing the loading state.
 *
 * @interface LoadingState
 * @property {boolean} isLoading - Indicates whether a loading process is currently active.
 */
interface LoadingState {
  isLoading: boolean;
}

/**
 * The initial state for the loading slice.
 *
 * @constant
 * @type {LoadingState}
 */
const initialState: LoadingState = {
  isLoading: false,
};

/**
 * A slice of the Redux store responsible for managing the loading state.
 *
 * This slice contains actions and reducers to show or hide loading indicators in the application.
 *
 * @constant
 * @type {Slice<LoadingState>}
 */
const loadingSlice = createSlice({
  name: 'loading',
  initialState,
  reducers: {
    /**
     * Sets the loading state to true, indicating that a loading process is active.
     *
     * @param {LoadingState} state - The current state of the loading slice.
     */
    showLoading: (state) => {
      state.isLoading = true;
    },

    /**
     * Sets the loading state to false, indicating that the loading process has finished.
     *
     * @param {LoadingState} state - The current state of the loading slice.
     */
    hideLoading: (state) => {
      state.isLoading = false;
    },
  },
});

/**
 * Action creators generated from the loading slice.
 *
 * @constant
 * @type {{ showLoading: () => PayloadAction<void>, hideLoading: () => PayloadAction<void> }}
 */
export const { showLoading, hideLoading } = loadingSlice.actions;

/**
 * The reducer for the loading slice, to be used in the Redux store.
 *
 * @constant
 * @type {Reducer<LoadingState>}
 */
export default loadingSlice.reducer;
